import { useState, type FC } from 'react';

import { Placeholder, useRendering } from '@eneco/cms';
import { DesktopHeader, MobileHeader, PageGrid, StickyHeader } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';

import { MAIN_NAVIGATION_SLOTS, resolveSlot } from './slots';

/**
 * `MainNavigation` block for the CMS-agnostic lane, serving **both** providers. Carries no fields of its
 * own on either provider — purely a structural container for its named regions (`logo`, `left`/`right`
 * navigation, …), same role as `Section`/`SplitView`.
 *
 * Built on the design system's own `StickyHeader`/`DesktopHeader`/`MobileHeader`, which is what the
 * production header app composes too (`apps/header/shared/multilabel/src/Header.tsx`). That app itself
 * is tagged `app:next` and outside this lib's module boundary (`nx.json`), but the Sparky components it
 * is built from are not — so the chrome here is the real one: sticky bar, hamburger-left on mobile with
 * the navigation in a dialog, hover dropdowns on desktop.
 *
 * Two deliberate differences from production, both because this lane is guest-only (`CmsRouteContainer`
 * mounts `DigitalCoreProvider` with an empty customer context and no session):
 *
 * - **The mobile profile button is inert.** `MobileHeader` always renders it, and there is no profile
 *   menu to open here. Its handler is a no-op rather than a dialog onto nothing.
 * - **No `SideNav`, `TopMenu` or `SecondaryMenu` sub-apps.** Those regions render whatever the CMS puts
 *   in the corresponding placeholder, or nothing.
 */
const MainNavigation: FC = () => {
  const rendering = useRendering();
  const isDesktop = useMediaQuery('lg');
  const [isHamburgerMenuOpen, setHamburgerMenuOpen] = useState(false);

  const logoSlot = resolveSlot(rendering, MAIN_NAVIGATION_SLOTS.logo);
  const leftSlot = resolveSlot(rendering, MAIN_NAVIGATION_SLOTS.left);
  const rightSlot = resolveSlot(rendering, MAIN_NAVIGATION_SLOTS.right);
  const topheaderSlot = resolveSlot(rendering, MAIN_NAVIGATION_SLOTS.topheader);
  const secondarySlot = resolveSlot(rendering, MAIN_NAVIGATION_SLOTS.secondary);
  const sideSlot = resolveSlot(rendering, MAIN_NAVIGATION_SLOTS.side);
  const breadcrumbsSlot = resolveSlot(rendering, MAIN_NAVIGATION_SLOTS.breadcrumbs);
  const headerLangSlot = resolveSlot(rendering, MAIN_NAVIGATION_SLOTS.headerLang);

  const logo = logoSlot ? <Placeholder name={logoSlot} /> : null;
  const navigation = leftSlot ? <Placeholder name={leftSlot} /> : null;
  const languageSelector = headerLangSlot ? <Placeholder name={headerLangSlot} /> : null;

  return (
    <>
      <StickyHeader>
        {isDesktop ? (
          <DesktopHeader>
            {topheaderSlot && <Placeholder name={topheaderSlot} />}
            <DesktopHeader.Main
              logoLink={logo}
              languageSelector={languageSelector}
              profileLink={rightSlot ? <Placeholder name={rightSlot} /> : null}
              // Mirrors `apps/header/shared/multilabel/src/Header.tsx`'s `isOpenDomain` (no secondary nav
              // → open domain). Required, not cosmetic: `styledMainRecipe` has no default variant, so
              // leaving it undefined renders the header with no background at all rather than white.
              isOpenDomain={!secondarySlot}>
              {navigation}
            </DesktopHeader.Main>
            {secondarySlot && <Placeholder name={secondarySlot} />}
          </DesktopHeader>
        ) : (
          <MobileHeader
            isHamburgerMenuOpen={isHamburgerMenuOpen}
            setHamburgerMenuOpen={setHamburgerMenuOpen}
            // No `hamburgerMenuLabel`: Sparky renders it as a visible caption under the icon, not as an
            // accessible name, and production passes it only where the CMS supplies a menu title.
            // Giving it one put "Main navigation" on screen next to the burger.
            // Guest-only lane: there is no profile menu behind this button. See the doc comment.
            isProfileMenuOpen={false}
            setProfileMenuOpen={noop}
            logoLink={logo}>
            <MobileHeader.HamburgerMenu isOpen={isHamburgerMenuOpen}>
              {topheaderSlot && <MobileHeader.Top>{<Placeholder name={topheaderSlot} />}</MobileHeader.Top>}
              <MobileHeader.Main>{navigation}</MobileHeader.Main>
              {(languageSelector || sideSlot) && (
                <MobileHeader.Bottom>
                  {languageSelector}
                  {sideSlot && <Placeholder name={sideSlot} />}
                </MobileHeader.Bottom>
              )}
            </MobileHeader.HamburgerMenu>
          </MobileHeader>
        )}
      </StickyHeader>
      {breadcrumbsSlot && (
        <PageGrid>
          <PageGrid.Item gridColumn="1/-1">
            <Placeholder name={breadcrumbsSlot} />
          </PageGrid.Item>
        </PageGrid>
      )}
    </>
  );
};

const noop = () => undefined;

export default MainNavigation;
